import HeroBusiness from "@/components/common/HeroBusiness";
import Container from "@/components/layout/Container";
import { Link } from "react-router-dom";

const features = [
  {
    icon: "account_balance_wallet",
    title: "충전 관리",
    items: [
      { label: "즉시 충전", desc: "필요시 실시간으로 즉시 충전하여 긴급 상황에 대응" },
      { label: "잔액 현황", desc: "실시간 잔액과 사용 가능 금액을 한눈에 파악" },
      {
        label: "충전 이력 관리",
        desc: "모든 충전 이력을 체계적으로 보관하고 필요시 엑셀 다운로드",
      },
    ],
  },
  {
    icon: "message",
    title: "서비스 타입별 문자 조회",
    items: [
      {
        label: "타입별 분류",
        desc: "SMS, LMS, MMS, 알림톡, RCS 등 서비스 타입별로 발송 내역 분류 조회",
      },
      { label: "상세 필터링", desc: "기간, 발송 상태, 수신 번호 등 다양한 조건으로 메시지 검색" },
      { label: "발송 결과 분석", desc: "성공/실패율, 비용 분석 등 발송 결과를 상세하게 리포팅" },
    ],
  },
  {
    icon: "insights",
    title: "실시간 통계 대시보드",
    items: [
      { label: "실시간 차트", desc: "시간대별, 일별, 월별 발송 현황을 시각적으로 표시" },
      { label: "통계 분석", desc: "발송량, 성공률, 비용 추이 등을 종합적으로 분석" },
    ],
  },
  {
    icon: "calculate",
    title: "정산 자동화",
    items: [
      { label: "자동 정산", desc: "매월 자동으로 사용 내역을 정산하고 청구서 생성" },
      { label: "세금계산서 발행", desc: "전자 세금계산서를 자동으로 발행하여 회계 업무 간소화" },
      { label: "정산 내역 조회", desc: "정산 내역을 언제든지 확인하고 다운로드" },
    ],
  },
];

const BusinessAdmin = () => {
  return (
    <Container>
      <HeroBusiness title="스마트한 정산 관리로" subtitle="비즈니스 효율 극대화" />

      <section id="main-content">
        <div className=" mx-auto px-8 lg:px-[8rem] py-[70px]">
          {/* Page Header */}
          <div className="mb-16">
            <p className="text-[1.4rem] text-gray-5 mb-2">관리자 정산 시스템 ⬥</p>
            <h2 className="text-[3.2rem] font-bold mb-6">
              자동화 정산 및
              <br />
              경영 지원 시스템
            </h2>
            <p className="text-[1.6rem] text-gray-5 leading-[1.8]">
              실시간 데이터 분석과 자동화된 정산 프로세스로 업무 효율성을 높이고,
              <br />
              정확한 경영 의사결정을 지원하는 통합 관리 플랫폼입니다.
              <br />
              복잡한 충전 및 사용 내역을 한눈에 파악하고, 체계적으로 관리할 수 있습니다.
            </p>
          </div>

          <div className="w-full h-[1px] bg-gray-2 mb-16"></div>

          {/* System Description */}
          <section className="mb-20">
            <h3 className="text-[2.4rem] font-bold mb-6">관리자 정산 시스템</h3>
            <p className="text-[1.6rem] text-gray-6 leading-[1.8] mb-12">
              스탠다드네트웍스의 관리자 정산 시스템은 메시징 서비스의 충전, 사용, 정산 과정을
              실시간으로 모니터링하고 관리할 수 있는 통합 플랫폼입니다.
              <br />
              자동화된 프로세스와 직관적인 인터페이스로 업무 시간을 단축하고, 정확한 데이터 기반의
              경영 의사결정을 지원합니다.
            </p>

            {/* Features Grid */}
            <h4 className="text-[2rem] font-semibold mb-8">주요 기능</h4>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="transition-transform bg-white border shadow-sm border-gray-2 rounded-xl p-7 hover:-translate-y-1"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="material-icons-round text-[3.2rem] text-navy">
                      {feature.icon}
                    </span>
                    <h5 className="text-[1.8rem] font-bold text-navy">{feature.title}</h5>
                  </div>
                  <ul className="space-y-2">
                    {feature.items.map((item, itemIdx) => (
                      <li
                        key={itemIdx}
                        className={`py-2 ${itemIdx < feature.items.length - 1 ? "border-b border-gray-1" : ""}`}
                      >
                        <strong className="text-[1.4rem] text-navy">{item.label}</strong>
                        <p className="text-[1.4rem] text-gray-5 leading-[1.6]">{item.desc}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <p className="text-[2rem] font-bold mb-2">💡 지금 바로 시작하세요!</p>
            <p className="text-[1.5rem] text-gray-5 leading-[1.8] mb-8">
              관리자 정산 시스템은 별도의 설치 없이 웹 브라우저를 통해 언제 어디서나 접속
              가능합니다.
              <br />
              직관적인 UI/UX로 누구나 쉽게 사용할 수 있으며, 전문 고객지원팀이 24시간 도움을
              드립니다.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/service/submit" className="btn-primary">
                서비스 신청하기
              </Link>
              <a
                href="https://customer.std-ums.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center min-h-[48px] px-8 py-3 rounded-full text-[1.6rem] font-medium bg-white text-navy border-2 border-navy shadow-sm hover:-translate-y-0.5 transition-transform"
              >
                바로가기
                <span className="material-icons-round ml-2 text-[2rem]">arrow_outward</span>
              </a>
            </div>
          </section>
        </div>
      </section>
    </Container>
  );
};

export default BusinessAdmin;
